import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Landing } from '../components/Landing'
import { asset } from '../lib/asset'
import { Hero } from '../components/Hero'
import { History, HistoryPanel } from '../components/History'
import { summarizeOutcome } from '../copy/explain'
import { DiagnosisCards, DiagnosisStrip } from '../components/DiagnosisCards'
import { DecisionTrace } from '../components/DecisionTrace'
import { ConfirmGate, RegenGate, RegisterFirstDialog, WaitlistDialog } from '../components/Gates'
import { ProblemMap } from '../components/ProblemMap'
import { RoundCurve } from '../components/Rounds'
import { ResultCompare } from '../components/ResultCompare'
import { Fold, MiniHead, Review, Section } from '../components/Review'
import { YourImage } from '../components/YourImage'
import { ArtifactList } from '../components/ArtifactList'
import { DEMO_RUNS, PLACEHOLDER_PRICING } from '../mock/runs'
import type { DemoRun } from '../mock/runs'
import type { Artifact, Diagnosis } from '../types'
import {
  OVER_CAP_EXAMPLE, artifactDownloadUrl, artifactThumbUrl, artifactUrl, attachJob,
  bundleUrl, continueRound, inputSoftUrl, inputUrl, reportUrl, rerunJob,
  seedSnapshot, startDemoJob, startJob,
} from '../lib/jobClient'
import type { JobHandle, JobPhase, JobSnapshot } from '../lib/jobClient'
import { pickImage } from '../lib/pickImage'
import type { PickError, PickedImage } from '../lib/pickImage'
import { authHeaders, hasToken, loginUrl, registerUrl } from '../lib/account'
import { readJobId, writeJobId } from '../lib/jobUrl'
import { fetchWaitlist, joinWaitlist } from '../lib/waitlist'
import type { WaitlistState } from '../lib/waitlist'
import { IconAlert, IconInfo, IconClock, IconSpinner, IconWand, MilliCoin } from '../icons'
import { DemoBar, SCREENS, demoEnabled, readUrl, writeUrl } from './DemoBar'
import type { ScreenId, Session } from './DemoBar'

/*
 * 整页。
 *
 * 背景：在此之前这里是 PlayGround——九个状态各渲染一遍、顶部切换（规则 3.1 要求
 *   动效先做静态页）。静态页该验的都验过了，但「上传之后会发生什么」始终没有走过一遍：
 *   九张屏之间的转场、什么时候能换图、拒收的图长什么样，都还只是设想。
 * 设计意图：合成一页，状态由用户的动作推动。九个状态没有丢，它们变成了这一页
 *   在不同时刻的样子，`?state=` 仍然直达（见 DemoBar 的 SCREENS）。
 * 关键约束：
 *   1. **真作业与录像并存，而且必须分得清**。用户上传走的是真链路（广州换票 → 首尔跑管线
 *      → SSE 回传），读数、图、产物全部属于他自己那张图；`?state=` 直达的那十一屏仍然是
 *      录像，那时页面必须挂出出处条（DemoNote），否则等于拿别人的读数冒充用户的结论。
 *      判据是快照上的 `demo` 标志，不是「后端接没接上」。
 *   2. 处理中不给「换一张图」（PRD 7.3）：那时米粒已预扣，误点等于白花一次钱。
 *   3. 各屏的构成照 PRD 7.3，不在这里自行增减。顺序与排布可以变：作业的每一屏
 *      现在都是图占满宽、读数与决定贴在图底下（见 Review），7.3 里竖着列的那些东西
 *      一件没少，只是不再各自报一次名。
 *   4. 演示条只在钉了参数时出现（?state= / ?as= / ?pg=1）。真实用户看不到它——
 *      一个能凭空把自己切成「已登录 900 米粒」的按钮，摆在真链路旁边只会招来误会。
 */

export function RipplePage({ session, onSession: setSession }: {
  /** 登录态由外壳持有：导航要按它显示余额还是登录（见 App）。 */
  session: Session
  onSession: (s: Session) => void
}) {
  // 每次渲染现读一遍 URL。写参数走的是 replaceState（不触发渲染），
  // 而每次写之前都有一次 setState，所以下一帧读到的就是刚写进去的那份。
  const url = readUrl()
  const [runId, setRunId] = useState<DemoRun['id']>(url.run)
  const [picked, setPicked] = useState<PickedImage | null>(null)
  const [pickErr, setPickErr] = useState<PickError | null>(url.screen === 'failed' ? OVER_CAP_EXAMPLE : null)
  const [snap, setSnap] = useState<JobSnapshot | null>(() => {
    const seeded = seedFor(url.screen, url.run)
    if (seeded) return seeded
    // 地址栏带着作业号：第一帧就摆出「这一趟还在」的样子。等 attachJob 把快照取回来
    // 再说细节；不这么做的话，刷新之后会先闪一下「选一张图」，正是用户说的那个「没了」。
    const id = demoEnabled() ? null : readJobId()
    return id
      ? { id, phase: 'queued', steps: [], running: true,
          diag: null, after: null, error: null, holdExpiresAt: null }
      : null
  })
  const job = useRef<JobHandle | null>(null)
  const run = DEMO_RUNS[runId]
  /**
   * 地址栏钉了演示参数（?demo= / ?state= / ?as= / ?run= / ?pg=）：这一趟从头到尾放录像。
   * 判据与演示条是否出现共用一个函数——两处若各判各的，就会出现「有演示条却在真烧算力」
   * 或者反过来的组合，而那两种都会骗到人。
   */
  const demoPinned = demoEnabled()

  /**
   * 收快照。真作业一拿到作业号就把它写进地址栏（2026-09-11「URL 要变换，不然刷新就没了」），
   * 于是刷新、存书签、发给人看都接得回去（见 jobClient 的 attachJob）。
   * 录像那一卷不写：它的号在服务端并不存在，写进去只会让人刷出一堵墙。
   */
  const onSnap = useCallback((s: JobSnapshot) => {
    setSnap(s)
    if (s.demo) return
    // 接回来的那一趟发现作业已经被清掉了：地址栏那一截也一并清掉，
    // 不然每次刷新都撞同一堵墙，而用户看不出那堵墙是地址栏带来的。
    writeJobId(s.error?.code === 'job_gone' ? null : s.id || null)
  }, [])

  /* 换图要把上一个作业取消掉：米粒还预扣着，不取消就是让它白跑完。 */
  const stop = useCallback(() => {
    job.current?.dispose()
    job.current = null
  }, [])
  /*
   * 卸载只松手，不取消（见 JobHandle.detach）。作业活得比这一页长——地址栏里记着号，
   * 刷新之后接回来接着看。卸载就取消的话，刷新一下等于把一趟已经预扣过米粒的作业杀掉。
   */
  useEffect(() => () => {
    job.current?.detach()
    job.current = null
  }, [])

  const reset = useCallback(() => {
    stop()
    setSnap(null)
    setPickErr(null)
    setPicked(p => {
      if (p) URL.revokeObjectURL(p.objectUrl)
      return null
    })
    writeUrl({ screen: null })
    writeJobId(null)
  }, [stop])

  /* 「我处理过的」那层抽屉开着没有。不写进地址栏：它是翻找途中的一个动作，
     不是一个待得住的位置，刷新之后该回到那一趟作业本身。 */
  const [histOpen, setHistOpen] = useState(false)

  /**
   * 开页时把地址栏上那一趟作业接回来（2026-09-11「URL 要变换，不然刷新就没了」）。
   *
   * 跑完的由服务端从盘上重放，没跑完的接着订 SSE——停在闸上的那一趟刷新之后
   * 仍然停在闸上，倒计时接着走（`hold_expires_at` 在快照里）。
   * 钉了演示参数就不接：那一趟从头到尾放录像，接一个真作业进来等于两卷片子叠在一起。
   */
  useEffect(() => {
    if (demoPinned) return
    const id = readJobId()
    if (!id || job.current) return
    job.current = attachJob(id, onSnap)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * 从「我处理过的」里点开某一趟（2026-09-11「登录之后，要能够查看我的历史」）。
   *
   * 与刷新那条路走的是同一个 attachJob：作业号写进地址栏，快照取回来，没跑完的接着订 SSE。
   * 不整页跳转（`location.search = …`）是因为这样能省掉一次白屏，而地址栏的结果一模一样。
   * 手里那张 blob 图要清掉：它属于上一次选的图，留着的话对比板左边摆的是另一趟的原图。
   *
   * 手里那趟只松手，不取消（detach，不是 dispose）。抽屉现在能在作业跑着的时候拉开
   * （2026-09-11「历史记录只能首页看？」），若在这儿取消，翻一眼旧图就把一趟
   * 已经预扣过米粒的作业杀了；松手之后它照跑，在列表里是「还在跑」，回得去。
   * 「换一张图」那条仍然取消——那句话的意思本来就是「这趟不要了」。
   */
  const openFromHistory = useCallback((id: string) => {
    job.current?.detach()
    job.current = null
    setHistOpen(false)
    setPicked(p => {
      if (p) URL.revokeObjectURL(p.objectUrl)
      return null
    })
    setPickErr(null)
    job.current = attachJob(id, onSnap)
  }, [onSnap])

  /**
   * 上传前的门禁。两道，顺序固定：**先注册，再排队。**
   *
   *   1. 没注册就不放行（法规要求先做真实身份信息认证，见 RegisterFirstDialog）。
   *   2. 注册了也未必能用：功能还没对外开放时只收排队名单（见 WaitlistDialog）。
   *
   * 顺序不能对调：没注册的人排不了队（通知靠账号上的手机号或邮箱），
   * 先给他看排队屏，他点了按钮才被告知要先注册，等于两道门叠着开。
   * 服务端那侧是同一个顺序（gateway/routes/ripple.py 的换票）。
   *
   * 注册那道的判据是「有没有令牌」而不是「session 是不是 user」：开页时要向网关问一次
   * 余额（见 App 的 probing），那一个来回里 session 还是访客，此时若按 session 判，
   * 一个已登录的人手快点下去就会被弹窗拦住。带着令牌的人一律放过——令牌真的过期了，
   * 网关会回 registration_required，那一刻再把这一屏弹出来（下面那个 effect）。
   *
   * 钉了演示参数时两道都不拦：截图脚本要拍那十一屏，拦了就一张也拍不出来。
   */
  const registered = session.kind === 'user' || hasToken() || demoPinned
  const [askRegister, setAskRegister] = useState(false)
  const [wait, setWait] = useState<WaitlistState>({ open: true, joined: false })
  const [askWait, setAskWait] = useState(false)

  /* 开没开放由服务端说（开关在网关的配置表里，改一个字段就开，不用发版）。
     只在开页问一次：开放这件事一天内只会发生一次，为它轮询不值当。
     问不到就当开着——真正拦人的是换票那一跳，它会回 not_open_yet。

     没令牌的人不问：他先撞注册闸，开没开放对他这一步毫无影响。首屏是公开页面，
     访客占绝大多数，为一个用不上的答案给网关加一次请求不值当。 */
  useEffect(() => {
    if (demoPinned || !hasToken()) return
    const abort = new AbortController()
    fetchWaitlist(authHeaders(), abort.signal).then(s => {
      if (!abort.signal.aborted) setWait(s)
    })
    return () => abort.abort()
  }, [demoPinned])

  const guard = useCallback(() => {
    if (!registered) {
      setAskRegister(true)
      return false
    }
    if (!wait.open) {
      setAskWait(true)
      return false
    }
    return true
  }, [registered, wait.open])

  const onJoin = useCallback(async () => {
    const ok = await joinWaitlist(authHeaders())
    if (ok) setWait(s => ({ ...s, joined: true }))
    return ok
  }, [])

  /* 换票被拒时把对应的那张弹窗摆出来，省得他对着一句话找不到下一步在哪：
       · registration_required —— 令牌过期、被撤销，或有人绕过页面直接打接口。
       · not_open_yet —— 开页问到的状态过时了（开关刚翻、或那次问没问到）。
     两处都以服务端的回答为准，页面那份状态只是用来少走一趟白跑的上传。 */
  useEffect(() => {
    const code = snap?.error?.code
    if (code === 'registration_required') setAskRegister(true)
    if (code === 'not_open_yet') {
      setWait(s => ({ ...s, open: false }))
      setAskWait(true)
    }
  }, [snap?.error?.code])

  /** 用户选了文件：本地先按服务端那三条判一遍，过了才建作业。 */
  const onPick = useCallback(async (f: File) => {
    if (!guard()) return
    stop()
    setSnap(null)
    const r = await pickImage(f)
    if ('err' in r) {
      setPickErr(r.err)
      setPicked(null)
      return
    }
    setPickErr(null)
    setPicked(prev => {
      if (prev) URL.revokeObjectURL(prev.objectUrl)
      return r.ok
    })
    writeUrl({ screen: null })
    // 真链路：换票在广州、跑图在首尔，进度靠 SSE 回来。钉了演示参数时仍然放录像，
    // 否则截图脚本每拍一屏都要真烧一次算力。
    //
    // 第二个参数是「按含重画的那一档去换票」，判据同样是**有没有令牌**而不是
    // session：开页问余额的那一个来回里 session 还是访客，此时按 session 判，
    // 手快的人换到的是只到净化那一档的票，而他的图若真需要重画，就会一路跑到
    // 「交给人判断」才停，那一屏上没有授权按钮。有令牌就照登录用户问，
    // 令牌算不算数由网关说（它会回 401，那时弹注册闸）。
    const asUser = session.kind === 'user' || hasToken()
    job.current = demoPinned
      ? startDemoJob(run, setSnap)
      : startJob(r.ok.file, asUser, onSnap)
  }, [run, session, stop, demoPinned, guard])

  /**
   * 「重新处理这张图」：同一张原图再跑一趟，不用用户回去翻文件。
   *
   * 2026-09-12 用户裁决：「这个不是重新处理吗？为什么还要重新上传啊」。停在半路的
   * 那几屏（等超时、中途失败、余额不够被降档）此前只给「重新上传这张图」——可那张图
   * 明明还在服务器上，作业目录留 24 小时（见 jobClient 的 rerunJob 与服务端的
   * `_prior_input`）。
   *
   * 手里那趟只松手，不取消：它已经到终态了（超时/失败都退过预扣），再发一次 cancel
   * 只是让服务端多接一个没人要的请求，而旧作业号仍要能打开——它记的是「那一趟等超时了」。
   *
   * 换票也要走一遍门禁：这是一趟新作业，要重新预扣米粒，注册与开放两道跟上传时同规矩。
   */
  const [rerunning, setRerunning] = useState(false)
  const rerun = useCallback((jobId: string) => {
    if (!guard()) return
    job.current?.detach()
    job.current = null
    setRerunning(true)
    setPickErr(null)
    const asUser = session.kind === 'user' || hasToken()
    job.current = rerunJob(jobId, asUser, onSnap)
  }, [guard, session, onSnap])

  /**
   * 「再重画一轮」：接着上一趟那张候选图往下推（2026-09-13 用户裁决）。
   *
   * 与「重新处理」共用同一段（松手、转圈、门禁、换票），区别只在服务端拿哪张图当输入
   * ——所以两者只差 continueRound 这一个调用，见 jobClient 那两个函数上面的注释。
   * 授权不在这里问第二遍：按钮旁边摆着这一趟的价钱，按下去就是授权（服务端在建作业
   * 那一刻落授权，见 app/api/jobs.py 的 continue_from）。
   */
  const continueRun = useCallback((jobId: string) => {
    if (!guard()) return
    job.current?.detach()
    job.current = null
    setRerunning(true)
    setPickErr(null)
    const asUser = session.kind === 'user' || hasToken()
    job.current = continueRound(jobId, asUser, onSnap)
  }, [guard, session, onSnap])
  /* 新作业号一到（或者换票就被拒，那时号变回空），按钮上那圈就该停。
     不清 snap 是有意的：换票加上传要几百毫秒，那期间页面留在原来那一屏上，
     比退回「选一张图」再跳回来稳当。 */
  useEffect(() => { setRerunning(false) }, [snap?.id])

  /** 演示条上切身份。 */
  const onSession = useCallback((s: Session) => {
    setSession(s)
    writeUrl({ session: s })
  }, [])

  /**
   * 诊断闸的「下一步」。`authorizeRegen` 是「这一屏上摆过价格、用户点了同意」，
   * 那就连重画那道一起放行（见 jobClient 的 proceed）。
   * 直达过来的那几屏没有活着的作业，照授权那条路现放一卷录像。
   */
  const onProceed = useCallback((authorizeRegen: boolean, deep = false) => {
    if (job.current) job.current.proceed(authorizeRegen, deep)
    else job.current = startDemoJob(run, setSnap, 'authorized')
  }, [run])

  /** 授权。同样要照顾直达过来的那一屏：没有活着的作业就从「已授权」那一刻起放。 */
  const onAuthorize = useCallback(() => {
    if (job.current) job.current.authorizeRegen()
    else job.current = startDemoJob(run, setSnap, 'authorized')
  }, [run])

  /** 换一卷录像等于换一张图，正在跑的那一条作废。 */
  const onRun = useCallback((id: DemoRun['id']) => {
    setRunId(id)
    writeUrl({ run: id })
    reset()
  }, [reset])

  /** 九屏直达。落到与真跑同形的快照上，所以截图与实际走一遍看到的是同一份渲染。 */
  const onScreen = useCallback((id: ScreenId) => {
    stop()
    const s = SCREENS[id]
    setPickErr(id === 'failed' ? OVER_CAP_EXAMPLE : null)
    setSession(s.session)
    setRunId(s.run)
    setSnap(seedFor(id, s.run))
    writeUrl({ screen: id, session: s.session, run: s.run })
  }, [stop])

  const phase: JobPhase | 'idle' = snap?.phase ?? 'idle'
  const busy = phase === 'queued' || phase === 'diagnosing' || phase === 'processing'

  return (
    <>
      {/* 演示条自己判断要不要出现（地址栏没提演示就整个不渲染，见 demoEnabled）。 */}
      <DemoBar
        session={session}
        run={runId}
        screen={url.screen}
        showScreens={url.pg}
        onSession={onSession}
        onRun={onRun}
        onScreen={onScreen}
      />

      {/* 作业一旦开跑，标题收成一行，让位给用户自己那张图的数字（见 Hero）。
          处理中连「换一张图」都不给：那时米粒已预扣，误点等于白花一次钱。
          「我处理过的」这颗一直在：首屏那一块此刻已经不在页面上，没有它就只能先把
          手里这趟丢掉才翻得到旧的（2026-09-11「历史记录只能首页看？」）。 */}
      {phase !== 'idle' && (
        <Hero compact onReset={busy ? undefined : reset}
              onHistory={demoPinned ? undefined : () => setHistOpen(true)} />
      )}

      {/* 出处条只跟着录像走：真作业的读数本来就属于用户那张图，不需要声明。
          拒收那一屏下面没有任何读数，也就没有出处要交代。 */}
      {snap?.demo && phase !== 'failed' && <DemoNote picked={picked} run={run} />}

      {phase === 'idle' && (
        <>
          {pickErr && <RejectCard err={pickErr} />}
          {/* 首屏把「做什么、怎么开始、修成什么样」装进同一屏：左边拖入区，
              右边真实案例的对比器。对比器整页只此一处（见 Landing 的注释）。 */}
          <Landing onPick={onPick} guard={guard} />
          {/* 历史摆在案例底下：先答「这工具做什么、修成什么样」，再给回去找旧东西那条路。
              没登录、没跑过的人这块整个不渲染（见 History）。钉了演示参数也不出现——
              录像那一卷是固定的十二屏，接一块真数据进去就不是同一卷片子了。 */}
          {!demoPinned && <History onOpen={openFromHistory} />}
        </>
      )}

      {snap && (
        <Job snap={snap} run={run} session={session} picked={picked}
             onProceed={onProceed} onAuthorize={onAuthorize}
             onReset={reset} onRerun={rerun} onContinue={continueRun} rerunning={rerunning}
             onQueue={() => setAskWait(true)} />
      )}

      {/* 历史抽屉同样挂在最外层：它要盖住的是整页，而不是某一屏里的一块。 */}
      <HistoryPanel open={histOpen} currentId={snap?.demo ? null : snap?.id}
                    onOpen={openFromHistory} onClose={() => setHistOpen(false)} />

      {/* 两道闸都挂在最外层而不是拖入区里面：作业跑到一半令牌过期、或者换票时才发现
          还没开放，那时首屏已经不在了，挂在里面就弹不出来。 */}
      <RegisterFirstDialog open={askRegister} onClose={() => setAskRegister(false)} />
      <WaitlistDialog
        open={askWait}
        joined={wait.joined}
        onJoin={onJoin}
        onClose={() => setAskWait(false)}
      />
    </>
  )
}

/**
 * 这份诊断在图上红得出东西吗（2026-09-11 用户裁决：「这个最好红色叠加一下」）。
 *
 * 三项全未检出时 ProblemMap 自己返回 null，那时该摆原图：一张没有任何标记的图
 * 配着「问题出在哪」，等于自己承认在凑版式。所以判据只能有一份——两处各判各的
 * 会出现「位子留了、图没画」。停在半路的那几屏也照这一份判（见 Stopped、DoneStalled）。
 */
function markedUp(diag: Diagnosis | null | undefined): boolean {
  return !!diag && (
    !!diag.scale_index.flagged_tile_boxes?.length
    || diag.lattice.detected
    || [...diag.granule.flat_windows, ...diag.granule.oriented_windows].some(w => w.flagged)
  )
}

/**
 * 半路停下的那几屏共用的骨架：图还在（标红的就摆标红那张），说明与下一步贴在图下，
 * 三项读数摊在底下。
 *
 * 2026-09-12 用户裁决：「用户没有深度生成，也应该可以看到标红的版本」。在此之前，
 * 等超时和中途失败这两屏只有一张白底说明卡——可诊断早就跑完了（它正是停下来
 * 等人的原因），页面却把标红图连着读数一起丢掉，于是这一分钟的算力什么也没留下，
 * 用户还会以为「什么都没做出来」。
 *
 * 没有诊断的那些（换票就被挡回来、图还没开始跑）照旧只出说明卡：那时确实没有图可标。
 */
function Stopped({ diag, evidence, children }: {
  diag: Diagnosis | null | undefined
  evidence: ReactNode
  children: ReactNode
}) {
  if (!diag) return <>{children}</>
  return (
    <Review image={evidence}>
      <DiagnosisStrip diag={diag} />
      {children}
      <Fold t="详细读数" defaultOpen>
        <DiagnosisCards diag={diag} />
      </Fold>
    </Review>
  )
}

/**
 * 半路停下的那几屏上那颗「再来一次」。
 *
 * 2026-09-12 用户裁决：「这个不是重新处理吗？为什么还要重新上传啊」。原图还在服务器上
 * （作业目录留 24 小时）时，这颗按钮就该自己把那张图再跑一遍，而不是把用户送回
 * 文件选择框——那张图可能是他一小时前从相机导出来的，路径早忘了。
 *
 * 四种情形只能退回「重新上传」，因为服务器手里确实没有那张图：
 *   · 录像那一卷（作业号在服务端并不存在）；
 *   · `job_gone` / `input_gone`——过了 24 小时，作业与原图都清理掉了；
 *   · 被门挡回来那几档（注册、还没开放、访客次数用完）：票都没换到，图压根没上传过。
 *
 * 「换一张图」不在这里给：它一直在标题栏上（见 Hero），这一屏只回答「这张图怎么办」。
 */
function Retry({ snap, allow, primary, onRerun, onReset, busy }: {
  snap: JobSnapshot
  /** 调用方那一屏自己的判据（比如失败屏要排掉被门挡回来的那几档）。 */
  allow: boolean
  primary: boolean
  onRerun: (jobId: string) => void
  onReset: () => void
  busy: boolean
}) {
  const gone = snap.error?.code === 'job_gone' || snap.error?.code === 'input_gone'
  const cls = primary ? 'btn btn-pri' : 'btn'
  if (!allow || gone || snap.demo || !snap.id) {
    return <button className={cls} onClick={onReset}>重新上传</button>
  }
  return (
    <button className={cls} disabled={busy} onClick={() => onRerun(snap.id)}>
      {busy ? <><IconSpinner size={14} />正在处理</> : '重新处理'}
    </button>
  )
}

/** 作业开跑之后的全部屏幕。构成与顺序照 PRD 7.3。 */
function Job({
  snap, run, session, picked, onProceed, onAuthorize, onReset, onRerun, onContinue,
  rerunning, onQueue,
}: {
  snap: JobSnapshot
  run: DemoRun
  session: Session
  /** 用户手里那张图（blob 地址）。有它就不必等服务端回显，见 YourImage。 */
  picked: PickedImage | null
  /** 放行诊断闸。参数是「这一屏上摆过价格、用户点了同意」。 */
  onProceed: (authorizeRegen: boolean, deep?: boolean) => void
  onAuthorize: () => void
  onReset: () => void
  /** 同一张原图再跑一趟（见 Retry 与 RipplePage 的 rerun）。 */
  onRerun: (jobId: string) => void
  /** 接着这一趟的候选图再重画一轮（见 RipplePage 的 continueRun）。 */
  onContinue: (jobId: string) => void
  rerunning: boolean
  /** 把排队闸重新叫出来。用户把它关掉之后，卡片上要留一条回去的路。 */
  onQueue: () => void
}) {
  const balance = session.kind === 'user' ? session.balance : null
  const src = sourcesOf(snap, run)
  /* 价目一律来自换票时网关给的那份；录像那条路没有网关，用占位价撑版式。
     一个数字都不在页面里写死：单价在管理后台随时可改。 */
  const pricing = snap.pricing ?? PLACEHOLDER_PRICING
  const cost = pricing.regen
  const marked = markedUp(snap.diag)

  /* 用户那张图从这里开始一直在页面上（2026-09-11 用户裁决：「图片要一直在界面上，
     不要消失」）。在此之前，排队、诊断、处理三屏都只有一条步骤轨迹，用户盯着一串
     滚动的日志等一分钟，中途完全看不到自己那张图——那时页面上没有任何东西能证明
     它收到的是他刚选的那张。优先用手里的 blob：不必等服务端回显，换图也不会闪。 */
  const mine = picked?.objectUrl ?? src.before
  /* 手里那份 blob 是本地的，用不上占位图；接回来的那一趟才要（原图跨境要几十秒）。 */
  const mineSoft = picked ? null : src.beforeSoft
  /** 诊断做完之后，左栏那块图：标出了红的看标记，什么都没标出来的看原图。 */
  const evidence = marked
    ? <ProblemMap diag={snap.diag!} src={src.before} soft={src.beforeSoft} />
    : <YourImage src={mine} soft={mineSoft} status="诊断完成" />

  switch (snap.phase) {
    case 'queued':
      // 这一屏通常活不过两秒。原先它摆着大标题、副标题和一段「票据已签、点数已预扣、
      // 这台机器同时只跑两张大图」——那是我们的内部账，用户只想知道还要不要等。
      // 话越多越像出了事，所以只留一行。
      return <YourImage src={mine} soft={mineSoft} status="排队中" busy />

    case 'diagnosing':
      return (
        <YourImage
          src={mine} soft={mineSoft} status="正在诊断" busy steps={snap.steps} live={snap.previewSrc}
          caption="整张图按原始尺寸逐处检查，不缩图，大约 1 分钟。"
        />
      )

    /*
     * 诊断出来了，停在这儿等人（2026-09-11 用户裁决：「在处理前，先给出诊断。
     * 用户点击了下一步之后，再继续。」）。在此之前只有要花米粒的那一档会停，
     * 其余的从上传一路跑到成品，诊断于是成了事后说明而不是一个岔口。
     *
     * 两个相位摆在一起，因为它们是同一屏的两种结尾：
     *   · awaiting_confirm —— 每个作业都会经过的那一停（服务端的 gate=confirm）。
     *     这张图要走深度修复时，价格就摆在这一屏上，点下去等于连授权一起给了；
     *     同一次处理不为同一件事停两次（见 worker 的 wait_for_authorization 开头）。
     *   · awaiting_regen —— 兜底。上面那一屏没摆过价格（诊断没判结构化），
     *     可管线跑着跑着还是要重画：那就照旧拦一次，钱不会在用户没看见价格时花出去。
     */
    case 'awaiting_confirm':
    case 'awaiting_regen': {
      // 「这张图要重画」由诊断的结构化标志预判。判错也不会多花钱：
      // 预判成不用重画、管线却要重画时，兜底的授权闸会拦下来。
      const deep = snap.phase === 'awaiting_regen'
        || (!!snap.diag?.flags.scale_structured && !snap.regenUnaffordable)
      return (
        /* 图占满宽，芯片贴在图下，决定再底下一条栏。要花米粒的那一档价格就在栏上。 */
        <Review image={evidence}>
          <DiagnosisStrip diag={snap.diag!} />
          {deep ? (
            <RegenGate
              diag={snap.diag}
              costPoints={cost}
              listPoints={pricing.regenList}
              balancePoints={balance}
              priceIsPlaceholder={snap.regenPoints == null}
              expiresAt={snap.holdExpiresAt}
              onAuthorize={snap.phase === 'awaiting_regen' ? onAuthorize : () => onProceed(true)}
              onDecline={onReset}
            />
          ) : (
            <ConfirmGate
              plan={planOf(snap.diag!)}
              expiresAt={snap.holdExpiresAt}
              onProceed={() => onProceed(false)}
              /* 主动加选深度修复（2026-09-11 用户裁决）。诊断说滤波够用，但要不要更彻底
                 是用户的事。票据没买到重画额度的那一趟不给这个选项——点了也只会被
                 服务端挡回来，摆在那儿等于骗他。录像那条路同样不给：它是一卷放好的带子，
                 分不出岔。 */
              onDeep={!snap.demo && !snap.regenUnaffordable ? () => onProceed(true, true) : null}
              deepPoints={cost}
              deepListPoints={pricing.regenList}
              deepPriceIsPlaceholder={snap.regenPoints == null}
              basePoints={pricing.freeApplied ? 0 : pricing.restore}
              freeLeft={pricing.freeLeft}
              freeApplied={pricing.freeApplied}
              onDecline={onReset}
            />
          )}
          {/* 诊断闸上这些数就是用户决定要不要继续处理的依据，不再默认藏起来。
              仍保留 details，长页面里用户看完可以自己收起。 */}
          <Fold t="详细读数" defaultOpen>
            <DiagnosisCards diag={snap.diag!} />
          </Fold>
        </Review>
      )
    }

    case 'expired':
      return (
        <Stopped diag={snap.diag} evidence={evidence}>
          <div className="verdict warn">
            <div className="verdict-top">
              <IconClock size={20} />
              <h3>等待确认超时，任务已终止</h3>
            </div>
            <p>
              等待人工确认超过 15 分钟限定时长，任务已自动终止。预扣额度已全额退还，未产生扣费。诊断结果已保留。
            </p>
            <div className="gate-acts" style={{ marginTop: 16 }}>
              <Retry snap={snap} allow primary busy={rerunning}
                     onRerun={onRerun} onReset={onReset} />
            </div>
          </div>
        </Stopped>
      )

    case 'processing':
      return (
        <YourImage
          src={mine} soft={mineSoft} status="正在处理" busy steps={snap.steps} live={snap.previewSrc}
          /*
           * 图下这行只答「还要等多久」和「走的是哪一档」，不报工艺名。
           * 原先轻档那句是「正在执行本地滤波降噪。」，用户：「什么是本地滤波降噪？
           * 一点都不产品化」（2026-09-12）——「本地」说的是我们在哪台机器上算，
           * 「滤波降噪」是工序名，两样都不是他此刻要判断的事。
           * 此刻在做的哪一步由图上那条玻璃条说（ACTION_INFO 的 doing），两处不重复。
           */
          caption={snap.steps.some(s => s.action === 'regenerate' || s.action === 'reference_clean')
            ? '正在重画整张画面，通常几十秒到几分钟。'
            : '正在把查出来的细网格和颗粒清掉，不重画画面。'}
        />
      )

    case 'done':
      // 「跑完了」不等于「有成品」。管线有一支是走完流程仍然交给人判断
      // （needs_human_decision：重画之后指数还在结构化线上，或者压根没授权重画），
      // 那一支没有成品、没有复诊读数。下面两屏都硬取 snap.after，撞上就整页白屏——
      // 用户看到的是「一直没动静」，其实作业早跑完了（2026-09-10 实跑撞到）。
      if (!snap.after) {
        return <DoneStalled snap={snap} src={src} balance={balance} onReset={onReset}
                            onRerun={onRerun} onContinue={onContinue}
                            rerunning={rerunning} />
      }
      return <Done snap={snap} src={src} />


    case 'failed': {
      // 「被门挡住」不是「失败」。两者摆同一张卡、同一个按钮的话，用户点「重新上传」
      // 还会被同样挡回来——他需要的那扇门（注册、登录）得给出来。
      //
      //   · registration_required：令牌过期或被撤销的人才会走到这里（没令牌的在选图那步
      //     就被拦了，见上面的 guard）。注册闸那张弹窗会同时弹出来，这张卡是它的背景，
      //     所以标题不能写「处理没做完」——他的图根本没开始处理。
      //   · not_open_yet：还轮不到他（算力不够，正在扩）。图没动过，钱也没动过。
      //   · trial_exhausted：访客那一档 2026-09-10 撤了，广州不再发这个码；
      //     留着是因为首尔那侧仍有一道按 IP 的限次（app/api/jobs.py），
      //     哪天有人拿旧票据打进来还得靠它。
      const code = snap.error?.code
      const barred = code === 'registration_required' || code === 'trial_exhausted'
        || code === 'not_open_yet'
      const title = code === 'registration_required' ? '使用此功能需先完成注册'
        : code === 'not_open_yet' ? '算力扩容排队中'
          : code === 'trial_exhausted' ? '今日免费诊断额度已用尽'
            : '处理未完成'
      /* 半路断掉的那一趟，诊断往往已经做完了（它是第一步）。那份标红图与读数照摆，
         不跟着失败一起丢（2026-09-12 用户裁决，见 Stopped）。被门挡回来的那几档
         压根没跑过诊断，Stopped 于是只出说明卡，版式与从前一样。 */
      return (
        <Stopped diag={snap.diag} evidence={evidence}>
          <RejectCard title={title} err={snap.error!} />
          <div className="gate-acts" style={{ marginTop: 16 }}>
            {code === 'registration_required' && (
              <a className="btn btn-pri" href={registerUrl()}>去注册</a>
            )}
            {code === 'not_open_yet' && (
              <button className="btn btn-pri" onClick={onQueue}>加入排队</button>
            )}
            {code === 'trial_exhausted' && (
              <a className="btn btn-pri" href={loginUrl()}>登录后继续</a>
            )}
            {/* 被门挡回来的那几档没有图可重跑（票都没换到），Retry 会自己退回
                「重新上传」——判据在它那儿，这里只把这一屏的情形告诉它。 */}
            <Retry snap={snap} allow={!barred} primary={!barred} busy={rerunning}
                   onRerun={onRerun} onReset={onReset} />
          </div>
        </Stopped>
      )
    }
  }
}

/**
 * 跑完了，但没有成品。
 *
 * 管线有一支是走完全程仍然把判断交回给人（`needs_human_decision`）：结构化的纹理
 * 重画一轮之后指数还在判定线上，而重画次数有上限；或者这一档压根没授权重画。
 * 那一支不产出成品，也没有成品的复诊读数——所以完成屏的那两个都渲染不了。
 *
 * 这一屏要回答三件事，缺一件用户就会来问：为什么没有成品、钱怎么算、现在能做什么。
 * 重画出来的候选图仍在产物清单里（管线判它不达标，但它是用户花钱换来的东西，
 * 不该藏起来），所以下载区照给。
 */
/**
 * 轨迹里那些文件名，换成可以点开的地址。
 *
 * 只认产物清单里有的名字：清单是下载接口认的同一份白名单，不在里面的点开就是 404。
 * 录像那一卷直接不给链接——它的文件名在服务端并不存在。
 */
function traceFiles(snap: JobSnapshot) {
  if (snap.demo) return undefined
  return (name: string) =>
    snap.artifacts?.some(a => a.name === name)
      ? { src: artifactUrl(snap.id, name), thumb: artifactThumbUrl(snap.id, name) }
      : null
}

function DoneStalled({ snap, src, balance, onReset, onRerun, onContinue, rerunning }: {
  snap: JobSnapshot; src: Sources
  /** 只用来判断够不够付下一轮（不够就换成去充值）。页面上不报余额。 */
  balance: number | null
  onReset: () => void
  onRerun: (jobId: string) => void
  onContinue: (jobId: string) => void
  rerunning: boolean
}) {
  const regenerated = snap.steps.some(s => s.action === 'regenerate')
  // 米粒不够被降档的那一趟，服务端只知道「这张票没有重画权限」，于是说「要你先授权」。
  // 可这一屏上没有授权按钮——照抄那句话等于让人去做一件做不到的事。
  const broke = !!snap.regenUnaffordable && !regenerated
  /*
   * 「再来一轮」：接着这一趟重画出来的那张候选图，再重画一轮（2026-09-13 用户裁决）。
   *
   * 只在**真重画过**的那一支给。没重画过的那几支（没授权、米粒不够）手上没有候选图，
   * 服务端接不下去（app/api/jobs.py 的 _prior_candidate 认产物白名单里的 *_regen<N>.png）。
   * 录像那一卷也不给：它没有真作业号，按下去等于向服务端要一个不存在的作业。
   *
   * 价钱是「一次分析 + 一轮重画」——它是一趟新作业，从诊断开始重新走一遍（预扣、
   * 退款、缓存全都按新作业算），不是把旧作业捡起来续跑。所以这里报的是两项之和，
   * 而授权闸上报的只有重画那一项（那一趟的分析已经付过了）。
   */
  const pricing = snap.pricing
  const nextCost = pricing ? pricing.restore + pricing.regen : null
  const canContinue = regenerated && !snap.demo && !!snap.id
    && snap.artifacts?.some(a => /_regen\d+\.png$/.test(a.name))
  const affordable = balance == null || nextCost == null || balance >= nextCost
  return (
    <>
      {/* 这一支没有成品，所以图位上摆的是原图本身。图仍然要在：用户刚被告知
          「没修好、钱退了」，此时页面上连他那张图都没有，会以为图也没了。
          检出了什么就红什么（2026-09-12 用户裁决：「用户没有深度生成，也应该可以
          看到标红的版本」）——这一支恰恰是没走深度修复的那一支，而诊断早就做完了，
          摆一张干净的原图等于把它藏起来。 */}
      <Review image={markedUp(snap.diag)
        ? <ProblemMap diag={snap.diag!} src={src.before} soft={src.beforeSoft} />
        : <YourImage src={src.before} soft={src.beforeSoft} status="原图（未修改）" />}>
        <div className="verdict warn">
          <div className="verdict-top">
            <IconAlert size={20} />
            <h3>{regenerated ? '深度修复未达到交付标准'
              : broke ? '需深度修复，当前米粒余额不足' : '处理未达到交付标准'}</h3>
          </div>
          <p>{broke
            ? '图像纹理已呈现结构化特征，滤波无法完全消除，需执行深度重绘。当前账户米粒不足以支持深度修复，处理已终止。'
            : snap.humanNote || '管线已执行全部可用工序，结果未达到交付标准。'}</p>
          {/* 每一轮的读数。它要紧跟在那句话后面：那句话报的是**现在**停在多少，
              而这一条回答的是「它一直在往下走吗」——两者合起来才回答得了
              「这是搞不定吗」（2026-09-13 用户实撞，见 RoundCurve）。 */}
          {snap.rounds && <RoundCurve rounds={snap.rounds} />}
          <p style={{ marginTop: 8 }}>
            <b>预扣额度已全额退还，原图未修改。</b>
            {regenerated && '已生成的重绘候选图像已保存在产物列表中供查阅。'}
          </p>
          <div className="gate-acts" style={{ marginTop: 16 }}>
            {broke && (
              <a className="btn btn-pri" href="https://miyang.cn/console/recharge">充值米粒</a>
            )}
            {/* 重画过、还差一点的那一趟，主按钮是「再来一轮」：接着刚才那张候选图往下推，
                而不是从原图重跑（那样前几轮的算力和米粒都白花，见 continueRound）。 */}
            {canContinue && affordable && (
              <button className="btn btn-pri" disabled={rerunning}
                      onClick={() => onContinue(snap.id)}>
                {rerunning ? <><IconSpinner size={14} />正在处理</> : <><IconWand size={16} />再重画一轮</>}
              </button>
            )}
            {canContinue && !affordable && (
              <a className="btn btn-pri" href="https://miyang.cn/console/recharge">
                <MilliCoin size={16} />米粒不足，去充值
              </a>
            )}
            {/* 米粒不够那一档是唯一「同一张图再跑一遍会不一样」的情形：充完值重新换票
                就买得到重画那一档了，而图还在服务器上（2026-09-12 用户裁决，见 Retry）。
                其余情形同一张图同样的设置只会走到同一个结论，所以只给换图。 */}
            {broke ? (
              <Retry snap={snap} allow primary={false} busy={rerunning}
                     onRerun={onRerun} onReset={onReset} />
            ) : (
              <button className={canContinue ? 'btn' : 'btn btn-pri'} onClick={onReset}>更换图像</button>
            )}
          </div>
          {/* 价钱排在按钮之后，与授权闸同一个规矩（2026-09-11 用户裁决：
              「米粒放在按钮之后」）。这一趟是新作业，所以报的是分析加重画两项之和。 */}
          {canContinue && nextCost != null && (
            <p className="bar-cost" style={{ marginTop: 10 }}>
              <span className="bar-cost-n">
                <MilliCoin size={13} inline />
                <b>{nextCost}</b>{' 米粒'}
              </span>
              <span className="rounds-cost-why">
                {'再来一轮是一趟新作业：先在这张候选图上重新分析一次，再重画一轮。'}
                {'没修好同样全额退还。'}
              </span>
            </p>
          )}
        </div>
        <Downloads snap={snap} src={src} />
      </Review>
      {/* 两个结尾同一个版式（2026-09-11 用户发现过这条缝），所以这一支也跟着
          把诊断摊到外面、轨迹留在折里。这一支尤其要把轨迹留着：用户会问
          「那它到底试过什么」。折起来，不删。 */}
      {snap.diag && (
        <Section t="诊断读数" note="三项指标与判定依据。">
          <DiagnosisCards diag={snap.diag} />
        </Section>
      )}
      <Fold major t="它试过什么"
            note="每一步的判据与它得出的结果，原样展开，包括最后为什么停下。">
        <DecisionTrace steps={snap.steps} fileUrl={traceFiles(snap)} />
      </Fold>
    </>
  )
}

/**
 * 拿到成品的那一屏。
 *
 * 一屏两种结局，差别只在**这次有没有重画**（成品尺寸变了就是重画过）：
 *   · 重画那一档：画面被整幅重做，对比板两侧看得出差别，标题报叠瓦指数降了多少。
 *   · 切格子那一档：频域无损切除，画面内容与尺寸都不变，肉眼看不出差别——
 *     所以要额外给一句「这一档已经是最好状态」，再给对比板多一档差分：
 *     把动过的像素点亮，实测值跟在那一档的图注里（见 DiffView）。
 *     否则页面写着「已处理」而眼睛里什么都没有，用户会判定我们什么都没做。
 *
 * 2026-09-11 用户裁决「太长了」之后，决策轨迹与处理前的读数折进页脚那一折——
 * 它们是事后依据，而拿到成品的人第一件事是下载，不是复读管线的自述。
 *
 * 同日：这一屏一度是 7:3 两栏（图左、读数与下载在右栏），而跑之前的那几屏已经改成
 * 图占满宽、读数贴在图底下。用户当场发现了这条缝：「结束之后的怎么又回到之前
 * 左右格式的版本了？？」——所以两个结尾（成品、失败）都跟着改成同一个版式。
 */
function Done({ snap, src }: { snap: JobSnapshot; src: Sources }) {
  const deep = src.regenerated
  /*
   * 结论那一句与逐项对照分开摆（2026-09-12 用户裁决：「排序还不太对」
   * 「他们几个能不能上下合并一下」「感觉有点重复」）。
   *
   * 此前逐项对照自成一排卡贴在图底下，而处理前的诊断又是一排卡摊在下面，
   * 同一项指标在一页上出现两次、两种顺序、两套名字（平坦区鳞片 / 鳞片纹理）。
   * 现在每项只有一张卡：前后读数在卡头，判定依据（举例图、实测明细）在它下面。
   * 图底下只留那一句「变成了什么样」——那是拿到图的人第一眼要的，
   * 它后面紧跟着下载，逐项的账在下面那一节里。
   */
  const { headline, deltas } = summarizeOutcome(snap.diag!, snap.after!)
  return (
    <>
      <Review
        image={(
          <ResultCompare
            before={snap.diag!} after={snap.after!}
            beforeSrc={src.compare?.beforeFull ?? src.before}
            afterSrc={src.result ?? src.before}
            /* 切格子那一档两侧看着一模一样（2026-09-11「这里好像看得很不明显啊」），
               所以板子上多一档差分，把动过的像素点亮（同日「最好给用户能看的是差分图」）。
               重画那一档不给：尺寸和内容都变了，逐像素相减没有意义。
               录像那一档也不给：mock 的前后两张是两次运行各自导出的 JPEG，
               相减出来是整幅画的鬼影，不是这一步动过的地方。 */
            diff={!deep && !snap.demo && src.result ? { readings: src.diff } : undefined}
          />
        )}
      >
        {/* 这一趟是抄上一次的（服务端按 sha256 认出同一张图）。必须说出来：
            页面上一切照旧、余额却没动，用户会以为我们漏扣了钱，或者结果是假的。 */}
        {snap.cached && (
          <div className="drop-note">
            <IconInfo size={16} />
            <span>
              {'该图像历史处理结果已就绪，已直接读取：'}
              <b>未重复处理，未扣除额度。</b>
            </span>
          </div>
        )}
        <p className="outcome-line">{headline}</p>
        {/* 切格子那一档肉眼看不出差别，所以要说一句「动了什么、没动什么」。
            实测的差值不在这儿：它跟着差分那一档，摆在图底下（2026-09-11 用户裁决
            「除了有叠加的，还有原图，还有差分图」——同一件事只给一处交互）。 */}
        {!deep && <p className="rail-note">已无损切除周期网格，图像细节与原始分辨率保持一致。</p>}
        <Downloads snap={snap} src={src} />
      </Review>
      {/* 诊断摊在外面，轨迹仍然折着（2026-09-12 用户裁决「处理前的诊断要单拎出来，
          放到外面」）。这两样原先同在一折里，但它们的分量不一样：三项指标是上面
          那句结论的对照本，看完成品的人会回头找它；七步轨迹是追问到底时才翻的。
          同日又把复诊读数那排卡并进了这三张里，所以这一节现在管前后两头。 */}
      <Section t="三项指标，处理前后"
               note="每项先给这一趟的变化，再给它处理前的判定依据：现象长什么样、实测多少。">
        <DiagnosisCards diag={snap.diag!} deltas={deltas} />
      </Section>
      <Fold major t="它是怎么判的"
            note="管线自己选的路径、依据，以及每一步得出的结果。">
        <DecisionTrace steps={snap.steps} fileUrl={traceFiles(snap)} />
      </Fold>
    </>
  )
}

/**
 * 诊断闸上那句「接下来会做什么」。
 *
 * 只说这张图接下来会被怎么处理，不复述诊断读数——读数就在这句话底下的三张卡里。
 * 三档按管线的选路写（见 ripple_restore 的动作集）：格子纹走频域切除，
 * 颗粒走净化，两样都没有就只补一遍验收。「深度修复」那一档不走这里，
 * 它有自己的一屏（价格摆在那儿）。
 */
function planOf(diag: Diagnosis): string {
  const f = diag.flags
  if (f.lattice && (f.granule_flat || f.granule_pervasive || f.granule_suspected)) {
    return '将执行频域滤波切除网格，并净化颗粒噪点；全程不重绘，画面细节与原图尺寸保持一致。'
  }
  if (f.lattice) {
    return '将执行频域滤波切除网格纹理；全程不重绘，画面细节与原图尺寸保持一致。'
  }
  if (f.granule_flat || f.granule_pervasive || f.granule_suspected) {
    return '将对颗粒噪点执行无损平滑净化；全程不重绘，画面细节与原图尺寸保持一致。'
  }
  return '各项指标均在安全阈值内，将复验后标准化输出；画面细节与原图尺寸保持一致。'
}

interface Sources {
  /** 用户原图。真作业取服务端留的那份输入，录像取 mock 里的静态图。 */
  before: string
  /**
   * 同尺寸的模糊占位图，先摆它、原图到了再换（见 lib/progressive 的 useSharpened）。
   *
   * 2026-09-12 实测：一张 2.6 MB 的原图从首尔取到浏览器要 41 秒。刷新一个已经诊断完的
   * 页面时，标红那一屏就这样开着半分多钟——红框已经画好，压在一张只到三分之一的图上。
   * 录像那一卷不需要（图是同域静态文件，几十毫秒）。
   */
  beforeSoft?: string | null
  compare?: { beforeFull?: string; afterFull?: string; beforeZoom?: string; afterZoom?: string }
  result?: string
  diff?: { maxLevels: number; meanLevels: number; changedPct: number }
  artifacts: Artifact[]
  /**
   * 这一趟走过深度修复（画面被重画过）。
   *
   * 判据是轨迹里有没有 `regenerate` 那一步，**不是**比对前后尺寸。尺寸曾经当过判据，
   * 结果是：出图接口偶尔给回与请求一致的尺寸，页面就把一张重画过的图当成无损那一档，
   * 于是挂出差分按钮（前后根本不是同一幅画，算不出来，点了一片空白），
   * 还在图下写着「画面内容和尺寸都没动」——那是对着一张 AI 重画的图说的
   * （2026-09-11 用户实跑撞到：「差分没显示东西」）。
   */
  regenerated: boolean
}

/**
 * 这一屏的图与产物从哪来。
 *
 * 真作业一律走服务端：原图是 `/jobs/{id}/input`，其余是白名单产物 `/artifacts/{name}`——
 * 文件名取自服务端给的清单，前端不拼名字（PRD 8.3 的路径穿越那一条）。
 * 录像走 mock/runs.ts 里那套静态图。两条路的字段形状相同，所以各屏的 JSX 只有一份。
 */
function sourcesOf(snap: JobSnapshot, run: DemoRun): Sources {
  if (snap.demo) {
    return {
      before: run.images?.beforeFull ?? asset('/mock/before_full.jpg'),
      compare: run.images ?? undefined,
      result: run.notchResult?.src,
      diff: run.notchResult?.diff,
      artifacts: run.artifacts,
      regenerated: run.needsRegen,
    }
  }
  const arts = snap.artifacts ?? []
  const at = (name: string) => (arts.some(a => a.name === name) ? artifactUrl(snap.id, name) : undefined)
  const primary = arts.find(a => a.primary)
  return {
    before: inputUrl(snap.id),
    beforeSoft: inputSoftUrl(snap.id),
    compare: {
      beforeFull: inputUrl(snap.id),
      afterFull: primary && artifactUrl(snap.id, primary.name),
      beforeZoom: at('zoom_before.png'),
      afterZoom: at('zoom_after.png'),
    },
    result: primary && artifactUrl(snap.id, primary.name),
    diff: snap.pixelDiff ?? undefined,
    artifacts: arts,
    regenerated: snap.steps.some(s => s.action === 'regenerate'),
  }
}

/**
 * 产物下载。
 *
 * 真作业直接开服务端那条白名单接口（浏览器自己负责存盘，响应带
 * Content-Disposition: attachment）。录像态没有真实文件，点了只在控制台留一行。
 *
 * 2026-09-10 用户裁决：产物不再限时清理——能拿到产物的都是登录用户（访客在登录闸就
 * 停下了），作业留在账号里，之后回来还能下载。原先这一屏写「保留 24 小时，请及时下载
 * 留存」，是在催一件本不必催的事。注销后的删除按主站隐私政策走（30 个自然日内）。
 *
 * ⚠️ 这句承诺是**盘**兑现的，不是首尔：首尔那台机器上的作业目录仍然 24 小时一清
 * （磁盘过 70% 压到 2 小时，见 lab_ripple_api/app/jobs/cleanup.py——35 GB 的盘装不下
 * 每趟两三百兆的产物）。2026-09-12 起每趟跑完都把产物交进用户的米羊盘，长期那一份
 * 在那儿。所以没进盘的那一趟不能沿用这句话，要当场说明（见下面的 driveMiss）。
 */
function Downloads({ snap, src }: { snap: JobSnapshot; src: Sources }) {
  // 一个产物都没有的那一趟（跑到一半就断了）不摆这个标题：底下空着的
  // 「产物下载」比不摆更让人以为出了错。
  if (!src.artifacts.length) return null
  return (
    <>
      {/* 产物已经进盘的那一趟（driveAt 是领到境内直取链接那一刻），要把「留在哪儿」
          指明白：盘 2026-09-12 对所有人开放了，
          在那儿能一件件看、整组打包拿走、也能删。没进盘就不提盘——那时点过去是空的。 */}
      <MiniHead
        t="产物下载"
        note={snap.driveAt ? (
          <>
            已经存进你的米羊盘，之后回来还能下载。
            <a href="https://miyang.cn/console/drive" target="_blank" rel="noreferrer"
               style={{ marginLeft: 6 }}>去盘里看 →</a>
          </>
        ) : snap.driveMiss && snap.cached ? (
          /* 复用上一趟产物的那一趟：东西在上一趟的文件夹里，这一趟不会再存一份。
             照上面那句报警是虚报——他的盘里其实有（2026-09-12 实跑：命中缓存的
             作业 drive=null，而同一张图上一趟的二十二件早就在盘里了）。
             ⚠️ 首尔补了「命中缓存也进盘」之后（runner.deliver_detached），
             这一支会越来越少走到，但旧作业仍然会落在这里。 */
          <>
            这一趟复用了你上一趟的产物（没花米粒），上一趟的已经在你的米羊盘里。
            <a href="https://miyang.cn/console/drive" target="_blank" rel="noreferrer"
               style={{ marginLeft: 6 }}>去盘里看 →</a>
          </>
        ) : snap.driveMiss ? (
          /* 没进盘那一趟要现在就说。服务器上这一份是临时的（24 小时后清掉），
             而「空间满了」是用户自己能解决的那一种——所以把下一步写出来。 */
          <span className="warn-note">
            {snap.driveMiss === 'quota_full'
              ? '这一趟没能存进你的米羊盘：云端空间满了。请现在就把要留的下载下来——服务器上这一份 24 小时后会清理掉。'
              : '这一趟没能存进你的米羊盘。请现在就把要留的下载下来——服务器上这一份 24 小时后会清理掉。'}
            <a href="https://miyang.cn/console/drive" target="_blank" rel="noreferrer"
               style={{ marginLeft: 6 }}>去清一清或扩容 →</a>
          </span>
        ) : '留在你的账号里，之后回来还能下载。'}
      />
      <ArtifactList
        artifacts={src.artifacts}
        onDownload={a => {
          if (snap.demo) return console.log('[demo] 下载', a.name, '（演示态没有真实文件）')
          // 异步：产物进了盘的话，这一步会先确认直取链接还没过期（链接两小时到期，
          // 而这一屏可能已经开了半天）。没进盘就是原来那条首尔的地址。
          void artifactDownloadUrl(snap.id, a.name).then(u => { window.location.href = u })
        }}
        onDownloadAll={() => {
          if (snap.demo) return console.log('[demo] 下载全部过程数据（演示态没有真实文件）')
          window.location.href = bundleUrl(snap.id)
        }}
        onReport={() => {
          if (snap.demo) return console.log('[demo] 下载报告（演示态没有真实作业）')
          window.location.href = reportUrl(snap.id)
        }}
      />
    </>
  )
}

/**
 * 拒收卡。
 *
 * 用户拖进来的图被挡下时，必须同时给出「你这张是多少、上限是多少、为什么不替你缩放」
 * 三件事（PRD 第 6 章）。hint 单独一行，因为它是唯一一句用户能照着做的话。
 */
function RejectCard({ err, title = '这张图没有被接收' }: { err: PickError; title?: string }) {
  return (
    <div className="verdict bad" style={{ marginBottom: 20 }}>
      <div className="verdict-top">
        <IconAlert size={20} />
        <h3>{title}</h3>
      </div>
      <p>{err.message}</p>
      <p style={{ marginTop: 8 }}><b>{err.hint}</b></p>
    </div>
  )
}

/**
 * 数据出处条。
 *
 * 这一条不是装饰，是这一版页面唯一诚实的地方：上传是真的（左边那些数字取自
 * 用户自己那张图），诊断与处理是放录像（右边那句话说的就是这件事）。
 * 后端接上之后整条删掉——那时读数本来就属于用户那张图，不需要声明。
 */
/** 一兆以下用 KB。手机截图常在几百 KB，显示成「0.0 MB」会让人以为文件读坏了。 */
const size = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`

function DemoNote({ picked, run }: { picked: PickedImage | null; run: DemoRun }) {
  return (
    <div className="demo-note">
      {picked && (
        <div className="demo-note-file">
          <img src={picked.objectUrl} alt="" />
          <div>
            <b>{picked.name}</b>
            <span>
              {picked.type} · {picked.pixels[0]}×{picked.pixels[1]}
              {'（'}{((picked.pixels[0] * picked.pixels[1]) / 1_000_000).toFixed(2)}{' MP）· '}
              {size(picked.bytes)}
            </span>
          </div>
        </div>
      )}
      <p>
        <IconAlert size={15} />
        <span>
          {'后端还没接上。'}
          {picked ? '你这张图只做了上传校验，' : ''}
          {'下面的读数与图来自 '}<code>{run.run}</code>{' 那一次真实管线运行，'}
          <b>不是你这张图的结论</b>{'。'}
          {run.traceRun ? '决策轨迹那一段这条链还没导出来，借的是 ' : ''}
          {run.traceRun ? <code>{run.traceRun}</code> : null}
          {run.traceRun ? ' 里同名的几步。' : ''}
        </span>
      </p>
    </div>
  )
}

/**
 * ?state= 落到快照上。
 *
 * idle 与 failed 都还没有作业——前者是没上传，后者是上传被挡在门外，
 * 两个都返回 null，页面停在首屏（failed 多一张拒收卡）。
 */
function seedFor(screen: ScreenId | null, run: DemoRun['id']) {
  if (!screen || screen === 'idle' || screen === 'failed') return null
  return seedSnapshot(SCREENS[screen].phase, DEMO_RUNS[run])
}

/*
 * 这里曾有一个 Section：kicker + 24 像素标题 + 一句说明，作业的每一屏都由它
 * 一段段垒起来。2026-09-11 用户裁决「太长了」之后，作业屏改成图占满宽（见 Review），
 * 段头换成折叠区里的小标题（MiniHead）——四个段头光边距就占掉近两百像素，
 * 而图底下那几条芯片自己就说清了是什么，不必各自再报一次名。
 * 首屏还在用 .sec-head 那套样式（见 Landing），所以 CSS 里那几条留着。
 */
